import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { authGate, requestToken, verifyToken, formatApiError } from "../../lib/api";

export default function AdminLogin() {
  const { signInSession, user, ready } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials"); // "credentials" | "code"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [gateToken, setGateToken] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (ready && user) navigate("/admin", { replace: true });
  }, [ready, user, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { gate_token } = await authGate(username.trim(), password);
      setGateToken(gate_token);
      await requestToken(gate_token);
      setStep("code");
      setCooldown(30);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      await requestToken(gateToken);
      setCooldown(30);
    } catch (err) {
      // gate token likely expired — return to credentials step
      setStep("credentials");
      setPassword("");
      setError("Session timed out. Please sign in again.");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await verifyToken(code.trim());
      signInSession(data.user, data.session_exp);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--vsl-bg)] text-[var(--vsl-fg)] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <img src="/logo-vessel.png" alt="VESSEL" className="h-8 mb-2" />
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] mb-12">Back Office</p>

        <AnimatePresence mode="wait">
          {step === "credentials" ? (
            <motion.form key="credentials" onSubmit={submitCredentials} data-testid="credentials-form"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
              <p className="font-mono text-xs leading-relaxed text-[var(--vsl-muted)] mb-8 inline-flex items-start gap-2">
                <Lock size={13} className="mt-0.5 shrink-0" />
                Sign in with your credentials.
              </p>
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Username</label>
              <input data-testid="username-input" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus required
                autoComplete="username" spellCheck="false"
                className="w-full bg-transparent border border-[var(--vsl-border)] px-4 py-3 font-mono text-sm outline-none focus:border-[var(--vsl-fg)] transition-colors mb-4" />
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Password</label>
              <input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                autoComplete="current-password"
                className="w-full bg-transparent border border-[var(--vsl-border)] px-4 py-3 font-mono text-sm outline-none focus:border-[var(--vsl-fg)] transition-colors" />
              {error && <p data-testid="credentials-error" className="font-mono text-xs text-red-400 mt-4">{error}</p>}
              <button data-testid="credentials-submit" type="submit" disabled={loading}
                className="group w-full mt-6 bg-[var(--vsl-fg)] text-[var(--vsl-bg)] font-mono text-xs tracking-[0.3em] uppercase py-4 inline-flex items-center justify-center gap-2 hover:bg-[var(--vsl-limestone)] transition-colors disabled:opacity-50">
                {loading ? "Verifying…" : "Continue"}
                {!loading && <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />}
              </button>
            </motion.form>
          ) : (
            <motion.form key="code" onSubmit={submitCode} data-testid="code-form"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
              <p className="font-mono text-xs leading-relaxed text-[var(--vsl-muted)] mb-8">
                A single-use code has been sent. Enter it below to access the back office.
              </p>
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Access code</label>
              <input data-testid="token-input" value={code} onChange={(e) => setCode(e.target.value)} autoFocus required
                autoComplete="off" spellCheck="false"
                className="w-full bg-transparent border border-[var(--vsl-border)] px-4 py-3 font-mono text-lg tracking-[0.35em] uppercase text-center outline-none focus:border-[var(--vsl-fg)] transition-colors" />
              {error && <p data-testid="verify-error" className="font-mono text-xs text-red-400 mt-4">{error}</p>}
              <button data-testid="verify-submit" type="submit" disabled={loading}
                className="w-full mt-6 bg-[var(--vsl-fg)] text-[var(--vsl-bg)] font-mono text-xs tracking-[0.3em] uppercase py-4 hover:bg-[var(--vsl-limestone)] transition-colors disabled:opacity-50">
                {loading ? "Verifying…" : "Access"}
              </button>
              <button type="button" data-testid="resend-code" onClick={resendCode} disabled={loading || cooldown > 0}
                className="w-full mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)] transition-colors disabled:opacity-40">
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Send a new code"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
